import { useRef, useCallback } from 'react';
import { RHYTHMS, NOTE_FREQUENCIES } from '../constants/gameConstants';

export const useRhythm = () => {
  const audioContextRef = useRef(null);
  const currentPatternRef = useRef(null);
  const timeoutsRef = useRef([]);

  // Initialize audio context
  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  // Play a single note with specific duration
  const playTone = useCallback((frequency, duration) => {
    const audioContext = getAudioContext();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);

    // Envelope for smooth attack/decay
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01); // Attack
    gainNode.gain.linearRampToValueAtTime(0.2, audioContext.currentTime + duration / 1000 - 0.05); // Sustain
    gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + duration / 1000); // Release

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + duration / 1000);

    return oscillator;
  }, [getAudioContext]);

  // Play a rhythm pattern
  const playRhythm = useCallback((rhythmId, noteFrequency = NOTE_FREQUENCIES['C4'], volumeMultiplier = 1.0) => {
    // Clear any existing timeouts
    timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    timeoutsRef.current = [];
    
    // Stop any currently playing pattern
    if (currentPatternRef.current) {
      currentPatternRef.current.forEach(osc => {
        try {
          osc.stop();
        } catch (e) {
          // Already stopped
        }
      });
      currentPatternRef.current = null;
    }

    const rhythm = RHYTHMS.find(r => r.id === rhythmId);
    if (!rhythm) return;

    const audioContext = getAudioContext();
    const startTime = audioContext.currentTime;
    const oscillators = [];
    let scheduleTime = 0;

    // Schedule each note in the pattern using Audio Context timing (not setTimeout)
    rhythm.pattern.forEach(duration => {
      const osc = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(noteFrequency, startTime + scheduleTime / 1000);

      // Envelope for smooth attack/decay - apply volume multiplier
      gainNode.gain.setValueAtTime(0, startTime + scheduleTime / 1000);
      gainNode.gain.linearRampToValueAtTime(0.3 * volumeMultiplier, startTime + scheduleTime / 1000 + 0.01);
      gainNode.gain.linearRampToValueAtTime(0.2 * volumeMultiplier, startTime + scheduleTime / 1000 + duration / 1000 - 0.05);
      gainNode.gain.linearRampToValueAtTime(0, startTime + scheduleTime / 1000 + duration / 1000);

      osc.connect(gainNode);
      gainNode.connect(audioContext.destination);

      osc.start(startTime + scheduleTime / 1000);
      osc.stop(startTime + scheduleTime / 1000 + duration / 1000);
      
      oscillators.push(osc);
      scheduleTime += duration;
    });

    currentPatternRef.current = oscillators;
  }, [playTone, getAudioContext]);

  // Stop any currently playing rhythm
  const stopRhythm = useCallback(() => {
    // Clear any pending timeouts
    timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    timeoutsRef.current = [];
    
    if (currentPatternRef.current) {
      currentPatternRef.current.forEach(osc => {
        try {
          osc.stop();
        } catch (e) {
          // Already stopped
        }
      });
      currentPatternRef.current = null;
    }
  }, []);

  return { playRhythm, stopRhythm };
};
